import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Coin, COINS_DATA } from '../../data/coins';
import { getUserAlbum } from '../lib/gameEngine';
import { SetSeriesItem } from '../lib/albumData';
import { AlbumGridView } from './AlbumGridView';
import { SetsAndSeriesView } from './SetsAndSeriesView';
import { AlbumTooltip } from './AlbumTooltip';
import './AlbumMenu.css';

interface AlbumMenuProps {
  onBack: () => void;
  onClaimSetReward?: (setId: string, rewardType?: 'circulation' | 'rare') => Promise<{ success: boolean; rewardAmount?: number; error?: string }>;
  initialUserAlbum?: Record<string, number>;
  initialSets?: SetSeriesItem[];
}

export const AlbumMenu: React.FC<AlbumMenuProps> = ({
  onBack,
  onClaimSetReward,
  initialUserAlbum,
  initialSets
}) => {
  const [mainTab, setMainTab] = useState<'album' | 'sets'>('album');
  const [userAlbum, setUserAlbum] = useState<Record<string, number>>(initialUserAlbum || {});
  const [serverSets, setServerSets] = useState<SetSeriesItem[]>(initialSets || []);

  // Tooltip state
  const [activeCoin, setActiveCoin] = useState<Coin | null>(null);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [isPinned, setIsPinned] = useState<boolean>(false);

  const loadAlbumData = useCallback(async () => {
    try {
      const result = await getUserAlbum();
      setUserAlbum(result.userAlbum);
      if (result.sets && result.sets.length > 0) {
        setServerSets(result.sets);
      }
    } catch (e) {
      console.error('Failed to load album data:', e);
    }
  }, []);

  useEffect(() => {
    if (!initialUserAlbum || Object.keys(initialUserAlbum).length === 0 || !initialSets || initialSets.length === 0) {
      loadAlbumData();
    }
  }, [loadAlbumData, initialUserAlbum, initialSets]);

  const handleClaimReward = useCallback(async (setId: string, rewardType: 'circulation' | 'rare' = 'circulation') => {
    if (!onClaimSetReward) return { success: false, error: 'Claim handler not provided' };
    const res = await onClaimSetReward(setId, rewardType);
    if (res.success) {
      await loadAlbumData();
    }
    return res;
  }, [onClaimSetReward, loadAlbumData]);

  // Stats
  const stats = useMemo(() => {
    const total = COINS_DATA.length;
    let owned = 0;
    for (const coin of COINS_DATA) {
      if ((userAlbum[coin.id] || 0) > 0) owned++;
    }
    const pct = total > 0 ? ((owned / total) * 100).toFixed(1) : '0.0';
    return { total, owned, pct };
  }, [userAlbum]);

  const handleHoverCoin = useCallback((coin: Coin, count: number, e: React.MouseEvent<HTMLElement>) => {
    if (isPinned) return; // Keep pinned coin visible until clicked elsewhere
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveCoin(coin);
    setActiveCount(count);
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 6 });
  }, [isPinned]);

  const handleLeaveCoin = useCallback(() => {
    if (isPinned) return;
    setActiveCoin(null);
    setTooltipPos(null);
  }, [isPinned]);

  const handleClickCoin = useCallback((coin: Coin, count: number, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveCoin(coin);
    setActiveCount(count);
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 6 });
    setIsPinned(true);
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setIsPinned(false);
    setActiveCoin(null);
    setTooltipPos(null);
  }, []);

  return (
    <div className="album-menu-container" onClick={handleBackgroundClick}>
      {/* Navigation */}
      <div className="album-nav-bar">
        <button className="back-btn" onClick={onBack}>
          ← Back to Main Menu
        </button>
      </div>

      {/* Header */}
      <div className="album-header-banner">
        <h1 className="album-title">Collection Album</h1>
        <div className="album-subtitle">
          Total Collected: <strong>{stats.owned}</strong> / {stats.total} ({stats.pct}%)
        </div>
      </div>

      {/* Primary Tabs */}
      <div className="album-main-tabs">
        <button
          className={`main-tab-btn ${mainTab === 'album' ? 'active' : ''}`}
          onClick={() => setMainTab('album')}
        >
          Album
        </button>
        <button
          className={`main-tab-btn ${mainTab === 'sets' ? 'active' : ''}`}
          onClick={() => setMainTab('sets')}
        >
          Sets & Series
        </button>
      </div>

      {/* Content */}
      <div className="album-content-body">
        {mainTab === 'album' ? (
          <AlbumGridView
            userAlbum={userAlbum}
            onHoverCoin={handleHoverCoin}
            onLeaveCoin={handleLeaveCoin}
            onClickCoin={handleClickCoin}
          />
        ) : (
          <SetsAndSeriesView
            userAlbum={userAlbum}
            sets={serverSets}
            onHoverCoin={handleHoverCoin}
            onLeaveCoin={handleLeaveCoin}
            onClickCoin={handleClickCoin}
            onClaimReward={handleClaimReward}
          />
        )}
      </div>

      {/* Floating Info Box */}
      <AlbumTooltip
        coin={activeCoin}
        count={activeCount}
        position={tooltipPos}
      />
    </div>
  );
};
